// Connexion Keycloak (OIDC) — plan notes/plans/PLAN_KEYCLOAK_STUDIO.md. Le front ne voit jamais
// le secret ni les jetons Keycloak de rafraîchissement : il crée state, nonce et code_verifier
// (PKCE S256), redirige vers Keycloak, puis le backend échange le code. Seul l'id_token revient,
// gardé pour terminer la session Keycloak à la déconnexion.

const PENDING_KEY = "oidc_pending";          // sessionStorage : {state, nonce, verifier, redirectUri}
const ID_TOKEN_KEY = "kc_id_token";          // localStorage : id_token_hint de la déconnexion
const END_SESSION_KEY = "kc_end_session";    // localStorage : end_session_endpoint du realm
const CLIENT_KEY = "kc_client_id";           // localStorage : client du realm (déconnexion)
// Codes d'erreur OIDC connus, affichés tels quels ; tout autre texte de l'URL est ignoré
const KNOWN_ERRORS = new Set(["access_denied", "login_required", "temporarily_unavailable", "interaction_required",
  "consent_required", "invalid_request", "server_error"]);

function base64url(bytes) {
  let s = "";
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomString(cryptoImpl = globalThis.crypto) {
  const bytes = new Uint8Array(32);
  cryptoImpl.getRandomValues(bytes);
  return base64url(bytes);
}

export async function pkceChallenge(verifier, cryptoImpl = globalThis.crypto) {
  const digest = await cryptoImpl.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function callbackUri(origin = window.location.origin) {
  return `${origin}/auth/callback`;
}

/** URL d'autorisation Keycloak ; garde state/nonce/verifier en sessionStorage. Rattachement
 *  manuel : `cfg.state`/`cfg.nonce` viennent du serveur (liés à la session Studio) et
 *  `intent` = "link". */
export async function buildAuthorizeUrl(cfg, { storage = window.sessionStorage, origin = window.location.origin, cryptoImpl = globalThis.crypto, intent = "login", returnTo = "/" } = {}) {
  const state = cfg.state || randomString(cryptoImpl);
  const nonce = cfg.nonce || randomString(cryptoImpl);
  const verifier = randomString(cryptoImpl);
  const redirectUri = callbackUri(origin);
  storage.setItem(PENDING_KEY, JSON.stringify({ state, nonce, verifier, redirectUri, intent,
                                                 returnTo: safeReturnPath(returnTo) }));
  const params = new URLSearchParams({
    client_id: cfg.client_id,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: cfg.scope || "openid email profile",
    state,
    nonce,
    code_challenge: await pkceChallenge(verifier, cryptoImpl),
    code_challenge_method: "S256",
  });
  return `${cfg.authorize_url}?${params.toString()}`;
}

/** Au retour : vérifie state et rend {code, verifier, nonce, redirectUri} ; efface l'attente
 *  dans tous les cas (un code ne se rejoue pas). null si state absent ou différent. */
export function consumeCallback(search, storage = window.sessionStorage) {
  const raw = storage.getItem(PENDING_KEY);
  storage.removeItem(PENDING_KEY);
  const q = new URLSearchParams(search);
  let pending = null;
  try { pending = raw ? JSON.parse(raw) : null; } catch { pending = null; }
  if (!pending || !q.get("code") || q.get("state") !== pending.state) return null;
  return { code: q.get("code"), verifier: pending.verifier, nonce: pending.nonce, redirectUri: pending.redirectUri,
           state: pending.state, intent: pending.intent || "login", returnTo: safeReturnPath(pending.returnTo) };
}

/** Chemin de retour après connexion (lien profond, règle d'Alex : on revient là où l'on allait).
 *  Uniquement un chemin RELATIF de ce site : refus de « // », des schémas, des antislashs et des
 *  caractères de contrôle (pas de redirection ouverte), et des pages de connexion (pas de boucle). */
export function safeReturnPath(path) {
  if (typeof path !== "string" || path.length === 0 || path.length > 2000) return "/";
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return "/";
  if (/[\u0000-\u001f\u007f]/.test(path)) return "/";
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(path)) return "/";
  const pathname = path.split(/[?#]/)[0];
  // « // » ou segments « . » / « .. » : un pathname qu'une relecture future pourrait faire sortir du site
  if (pathname.includes("//") || /(^|\/)\.\.?(\/|$)/.test(pathname)) return "/";
  const bare = pathname.toLowerCase().replace(/\/+$/, "");
  if (bare === "/login" || bare === "/auth" || bare.startsWith("/auth/")) return "/";
  return path;
}

/** Adresse de la page de connexion qui ramènera ensuite à `loc` (location du routeur ou window). */
export function loginPathFor(loc) {
  return loginPathTo(`${loc?.pathname || "/"}${loc?.search || ""}${loc?.hash || ""}`);
}

/** Même chose depuis un chemin (« /projects/x?tab=y »). */
export function loginPathTo(path) {
  const target = safeReturnPath(path);
  return target === "/" ? "/login" : `/login?next=${encodeURIComponent(target)}`;
}

/** Demande en cours (sans la consommer) : relancer une connexion ramène au même endroit. */
export function pendingRequest(storage = window.sessionStorage) {
  try {
    const p = JSON.parse(storage.getItem(PENDING_KEY) || "null");
    return { returnTo: safeReturnPath(p?.returnTo), intent: p?.intent || "login" };
  } catch {
    return { returnTo: "/", intent: "login" };
  }
}

// Retours de Keycloak qu'une NOUVELLE demande règle, sans rien montrer : onglet de connexion resté ouvert
// trop longtemps (Keycloak « restart » -> temporarily_unavailable / authentication_expired, mesuré
// 2026-10-21), session du hub finie (login_required / interaction_required), état inconnu de cet onglet.
// Un vrai refus (access_denied, compte en attente, code refusé) garde le message.
const RESTARTABLE_ERRORS = new Set(["temporarily_unavailable", "login_required", "interaction_required"]);
const AUTO_RESTART_KEY = "sso_auto_restart";

export function isRestartableError(code) {
  return RESTARTABLE_ERRORS.has(code);
}

/** Une relance automatique par minute au plus (sinon Studio <-> Keycloak en boucle) : true = relancer. */
export function takeAutoRestart(storage = window.sessionStorage, now = Date.now()) {
  try {
    const last = storage.getItem(AUTO_RESTART_KEY);
    const d = now - Number(last);
    if (last !== null && d >= 0 && d < 60 * 1000) return false;
    storage.setItem(AUTO_RESTART_KEY, String(now));
    return true;
  } catch {
    return false;          // stockage indisponible : le message, comme avant
  }
}

// Garde anti-boucle (sessionStorage, par onglet). Partir chez Keycloak ne bloque JAMAIS : la règle
// d'Alex est « toujours la connexion centrale ». Seuls bloquent, une minute :
// - un ÉCHEC revenu de Keycloak ou de Studio (refus, compte en attente, erreur) -> la page de
//   connexion affiche le message au lieu de relancer ;
// - un retour sur /login juste après une connexion RÉUSSIE (une API qui renverrait 401 en boucle
//   ferait sinon rebondir Studio <-> Keycloak sans fin).
const AUTO_FAIL_KEY = "sso_auto_failed";
const AUTO_OK_KEY = "sso_auto_ok";
const AUTO_COOLDOWN_MS = 60 * 1000;

/** Faut-il partir chez Keycloak sans clic ? Pas si une session Studio existe déjà (jeton), pas
 *  pour un compte en attente, pas si l'on demande les autres modes (?methods=all), pas dans la
 *  minute qui suit un échec ou une connexion réussie revenue ici. */
export function shouldAutoSso(search, pendingApproval, { storage = window.sessionStorage,
  local = window.localStorage, now = Date.now() } = {}) {
  if (pendingApproval || loggingOut) return false;
  // un jeton EXPIRÉ resté là (rafraîchissement impossible) ne retient pas : seule une session valable le fait
  const token = local.getItem("token");
  if (token) {
    const left = secondsLeft(token, now);
    if (left === null || left > 0) return false;
  }
  if (new URLSearchParams(search).get("methods") === "all") return false;
  const recent = (k) => {
    const d = now - Number(storage.getItem(k) || 0);
    return d >= 0 && d < AUTO_COOLDOWN_MS;          // horodatage futur (horloge reculée) : ignoré
  };
  return !recent(AUTO_FAIL_KEY) && !recent(AUTO_OK_KEY);
}

/** Retour de Keycloak en échec (refus, en attente, erreur, état invalide). */
export function markAutoSsoFailed(storage = window.sessionStorage, now = Date.now()) {
  storage.setItem(AUTO_FAIL_KEY, String(now));
}

/** Connexion réussie (sert seulement à détecter un rebond juste après). */
export function markAutoSsoSucceeded(storage = window.sessionStorage, now = Date.now()) {
  storage.setItem(AUTO_OK_KEY, String(now));
}

// Déconnexion en cours vers Keycloak (mémoire de la page, perdue au rechargement) : React affiche /login avant que
// le navigateur parte ; sans ce drapeau, la connexion automatique de /login remplaçait la navigation vers la
// déconnexion Keycloak (mesuré le 2026-10-21 : reconnexion silencieuse ou formulaire de Studio au retour).
let loggingOut = false;

export function markLoggingOut() {
  loggingOut = true;
}

export function isLoggingOut() {
  return loggingOut;
}

/** Page restaurée du cache du navigateur (« Retour » depuis Keycloak) : aucune déconnexion n'est plus en cours. */
export function clearLoggingOut() {
  loggingOut = false;
}

/** Tests seulement : remet le drapeau à zéro (sinon il survivrait d'un test à l'autre). */
export function resetLoggingOutForTests() {
  loggingOut = false;
}

/** Déconnexion VOLONTAIRE : la visite suivante repart tout de suite vers la connexion centrale. */
export function clearAutoSso(storage = window.sessionStorage) {
  storage.removeItem(AUTO_FAIL_KEY);
  storage.removeItem(AUTO_OK_KEY);
  storage.removeItem("sso_session_end_bypass");   // SESSION_END_KEY plus bas : une déconnexion voulue repart de zéro
}

// Session SSO fermée par Keycloak (déconnexion, back-channel, changement de rôle depuis le hub — Jimmy:Auth
// 2026-10-27) : le rafraîchissement lui-même est refusé, ce n'est pas un rebond d'API. La marque « succès récent »
// retenait alors /login sur un clic « Continuer avec Inevitable » ; on la lève, une fois par minute au plus
// (si Keycloak et l'API se renvoyaient la balle, la garde reprend la minute suivante).
const SESSION_END_KEY = "sso_session_end_bypass";

/** Réponse du rafraîchissement SSO qui dit « Keycloak a fermé la session » (code stable de l'API, jamais le texte). */
export function isSsoSessionEnded(err) {
  return err?.response?.status === 401 && err?.response?.data?.detail?.code === "oidc_session_ended";
}

/** Après un rafraîchissement refusé (401) : true = la connexion automatique de /login est rendue possible. */
export function allowAutoSsoAfterSessionEnd(storage = window.sessionStorage, now = Date.now()) {
  try {
    const last = storage.getItem(SESSION_END_KEY);
    const d = now - Number(last);
    if (last !== null && d >= 0 && d < AUTO_COOLDOWN_MS) return false;
    storage.setItem(SESSION_END_KEY, String(now));
    storage.removeItem(AUTO_OK_KEY);
    return true;
  } catch {
    return false;          // stockage indisponible : le clic, comme avant
  }
}

/** Annule une connexion en cours (retour en erreur) : rien ne reste en sessionStorage. */
export function dropPending(storage = window.sessionStorage) {
  storage.removeItem(PENDING_KEY);
}

/** Code d'erreur OIDC affichable, ou null (texte libre de l'URL : jamais affiché). */
export function safeErrorCode(code) {
  return KNOWN_ERRORS.has(code) ? code : null;
}

/** La connexion en cours est-elle une session SSO (rafraîchissable) ? */
export function isSsoSession(storage = window.localStorage) {
  return Boolean(storage.getItem(ID_TOKEN_KEY));
}

/** Oubli local d'une session SSO (connexion par un autre moyen, session refusée). */
export function forgetSession(storage = window.localStorage) {
  storage.removeItem(ID_TOKEN_KEY);
  storage.removeItem(END_SESSION_KEY);
  storage.removeItem(CLIENT_KEY);
}

/** Fuseau horaire de la personne (claim OIDC `zoneinfo` de l'id_token du hub, jimmy-l2s), ou undefined
 * (= fuseau du navigateur). Lu sans vérification : sert seulement à l'affichage des heures. Un nom qui n'est
 * pas un fuseau IANA connu du navigateur est ignoré. */
let zoneCache = { token: null, zone: undefined };   // un décodage par jeton, pas un par ligne affichée

export function userTimeZone(storage) {
  try {
    const token = (storage || window.localStorage).getItem(ID_TOKEN_KEY);
    if (!token) return undefined;
    if (token === zoneCache.token) return zoneCache.zone;
    let zone;
    try {
      const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      zone = typeof payload.zoneinfo === "string" ? payload.zoneinfo.trim() : "";
      if (zone) new Intl.DateTimeFormat("en-US", { timeZone: zone });    // RangeError si inconnu
      else zone = undefined;
    } catch {
      zone = undefined;
    }
    zoneCache = { token, zone };
    return zone;
  } catch {
    return undefined;          // stockage bloqué par le navigateur : fuseau du navigateur
  }
}

/** Options Intl + fuseau de la personne, sauf fuseau donné par l'appelant. Sans fuseau connu : celui du
 * navigateur (jamais un décalage fixe). Ici et pas dans utils.js : ce module n'importe rien (pas d'i18n). */
export function withUserZone(options) {
  if (options && options.timeZone) return options;
  const timeZone = userTimeZone();
  return timeZone ? { ...(options || {}), timeZone } : options;
}

/** Secondes avant l'expiration du JWT Studio (lu sans vérification : simple minuterie). */
export function secondsLeft(token, now = Date.now()) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp - now / 1000 : null;
  } catch {
    return null;
  }
}

export function rememberSession(idToken, endSessionUrl, clientId, storage = window.localStorage) {
  if (idToken && endSessionUrl) {
    storage.setItem(ID_TOKEN_KEY, idToken);
    storage.setItem(END_SESSION_KEY, endSessionUrl);
    if (clientId) storage.setItem(CLIENT_KEY, clientId);
  }
}

/** URL de fin de session Keycloak (et oubli local), ou null si la connexion n'était pas Keycloak. */
export function takeLogoutUrl({ storage = window.localStorage, origin = window.location.origin } = {}) {
  const idToken = storage.getItem(ID_TOKEN_KEY);
  const endSession = storage.getItem(END_SESSION_KEY);
  const clientId = storage.getItem(CLIENT_KEY) || "studio";
  forgetSession(storage);
  if (!idToken || !endSession) return null;
  const params = new URLSearchParams({
    id_token_hint: idToken,
    post_logout_redirect_uri: `${origin}/login`,
    client_id: clientId,
  });
  return `${endSession}?${params.toString()}`;
}
